import { PAGE_SIZE } from '@/constants/pageSize';
import {
  useDeleteDistributorEditableCircuit,
  useListDistributorEditableCircuits,
} from '@/hooks/useCircuits';
import useTableControls from '@/hooks/useTableControls';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import {
  EditableCircuit,
  editableCircuitColumns,
} from '@/types/circuits/EditableCircuitsColumnDef';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import StyledTable from '../styled/StyledTable';
import StyledNextPreviousButtons from '../styled/StyledNextPreviousButtons';
import { PATHS } from '@/constants/Paths';
import { useCallback, useState } from 'react';
import { Row } from '@tanstack/react-table';
import ModalDeletion from '../ui/ModalDeletion';
import { useToast } from '@/hooks/useToast';
import type { DistributorCircuitsListUrlState } from '@/utils/searchParams/buildDistributorCircuitsListSearch';

interface EditableCircuitsListProps {
  filters: {
    name: string;
    countries: string;
    durationDays: string;
    durationNights: string;
  };
  listSearchParams?: DistributorCircuitsListUrlState;
}

const EditableCircuitsList = ({
  filters,
  listSearchParams,
}: EditableCircuitsListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const getTranslatedConst = useTranslatedConst();
  const { page, setPage, sorting, setSorting, sortField, sortOrder } =
    useTableControls();
  const { data: editableCircuits } = useListDistributorEditableCircuits({
    page,
    pageSize: PAGE_SIZE,
    sortField,
    sortOrder,
    filters,
  });

  const [editableCircuitToDeleteId, setEditableCircuitToDeleteId] = useState<
    string | null
  >(null);
  const { mutateAsync: deleteDistributorEditableCircuit } =
    useDeleteDistributorEditableCircuit();
  const { toastAsync } = useToast();

  const goToEditableCircuit = useCallback(
    (row: Row<EditableCircuit>) => {
      navigate(
        PATHS.distributors.circuits.editableDetails.replace(
          ':editableCircuitId',
          row.original.id,
        ),
        listSearchParams ? { state: { listSearchParams } } : undefined,
      );
    },
    [navigate, listSearchParams],
  );

  const alertEnablePredicate = useCallback(
    (row: Row<EditableCircuit>) =>
      new Date(row.original.createdAt) <
      new Date(row.original.originalCircuit.updatedAt),
    [],
  );

  return (
    <div className="flex flex-col gap-4">
      <StyledTable
        columns={getTranslatedConst(editableCircuitColumns)}
        data={editableCircuits?.circuits ?? []}
        getRowId={(row) => row.id}
        sorting={sorting}
        setSorting={setSorting}
        onRowClick={goToEditableCircuit}
        onViewRow={goToEditableCircuit}
        onDeleteRow={(row) => setEditableCircuitToDeleteId(row.original.id)}
        alertEnablePredicate={alertEnablePredicate}
        alertMessage={t('distributorCircuits.alertMessage')}
      />
      <StyledNextPreviousButtons
        page={page}
        pageSize={PAGE_SIZE}
        setPage={setPage}
        dataLength={editableCircuits?.total ?? 0}
        label={t('distributorCircuits.list.circuit')}
      />
      {editableCircuitToDeleteId && (
        <ModalDeletion
          type="circuit"
          closeModal={() => setEditableCircuitToDeleteId(null)}
          actionOnClick={() =>
            toastAsync(
              deleteDistributorEditableCircuit(editableCircuitToDeleteId),
              {
                success: t('distributorCircuits.list.circuitRemovedSuccess'),
              },
            )
          }
        />
      )}
    </div>
  );
};

export default EditableCircuitsList;
